/*
  Red Bull Wings Cup Open: a paid native campaign for Red Bull Denmark,
  16–31 Oct 2026. Dates, markets, sign-up URLs, copy and images live here,
  so a date or market change is a one-line edit. Design source:
  wings-cup-banners.html (the "rbwc-" classes in wingsCup.css).
*/

/** Kill switch: false hides every unit, the ?rbwc= QA override included. */
export const WINGS_CUP_ENABLED = true;

/**
 * Sent as `live` on every campaign event, so test traffic before launch can
 * be filtered out. Set to true when the campaign goes live.
 */
export const WINGS_CUP_LIVE = false;

/**
 * Staging preview, so the partner sees the units before the campaign starts
 * without URL parameters: staging builds ignore the date window. The country
 * rule still holds there. Production and beta builds keep every rule.
 */
export const WINGS_CUP_PREVIEW =
  import.meta.env.VITE_APP_ENVIRONMENT === "staging";

export type WingsCupCountry = "DK" | "SE" | "NO";
export type WingsCupLang = "da" | "en";

export type WingsCupUnit =
  | "sbc"
  | "gallery"
  | "players"
  | "evo-players"
  | "evo-builder"
  | "navigation";

/**
 * What tracking reports. The menu is one unit for impressions ("navigation",
 * whichever of its link or card is seen first); clicks tell the two apart.
 */
export type WingsCupTrackedUnit =
  | WingsCupUnit
  | "navigation-card"
  | "navigation-link";

export const WINGS_CUP_CONFIG = {
  /** Shown as a box beside AD / REKLAME on every unit. */
  ageLimit: "18+",
  start: "2026-10-16T00:00:00+02:00",
  end: "2026-11-01T00:00:00+01:00", // exclusive: hides after Sat 31 Oct 23:59 Copenhagen time
  assets: {
    logo: "https://cdn.easysbc.io/campaigns/red-bull-wings-cup.png",
    // Natural size, so the image holds its space while it loads.
    logoWidth: 709,
    logoHeight: 709,
    fcPoints: "https://cdn.easysbc.io/campaigns/fc-points.webp",
    // Brand names, the same in every language.
    logoAlt: "Red Bull Wings Cup",
    fcPointsAlt: "FC Points",
  },
  markets: {
    DK: {
      url: "https://www.redbull.com/dk-da/events/red-bull-wings-cup-danmark-2026/red-bull-wings-cup-denmark-open-league",
    },
    SE: {
      url: "https://www.redbull.com/se-sv/events/red-bull-wings-cup-sverige/red-bull-wings-cup-sweden-open-league",
    },
    NO: {
      url: "https://www.redbull.com/no-no/events/red-bull-wings-cup-norway-2026/red-bull-wings-cup-open-league",
    },
  } satisfies Record<WingsCupCountry, { url: string }>,
  /** By the site language: Danish on the DA site, English on every other. */
  copy: {
    en: {
      tag: "AD",
      free: "Free to enter · Already live in FC 27",
      headline: "Win 1 match. Get <b>100 FC Points.</b>",
      headlineNav: "Win 1 match.<br>Get <b>100 FC Points.</b>",
      headlineEvoBuilder:
        "Want to do a paid evo?<br>Get <b>100 FC Points</b> with 1 win.",
      sub: "Red Bull Wings Cup Open: a free tournament mode in FC 27 on PS5. Up to 500 FC Points per bracket.",
      subEvoPlayers:
        "Red Bull Wings Cup Open on PS5. Up to 500 FC Points per bracket for your next evo.",
      subNav:
        "Red Bull Wings Cup Open on PS5. Up to 500 FC Points per bracket. Ends 31 Oct.",
      badge: "first win",
      cta: "Sign up for free →",
      menuHeading: "Tournaments",
      menuItem: "Wings Cup Open",
      menuBadge: "FREE",
    },
    da: {
      tag: "REKLAME",
      free: "Gratis at deltage · Allerede live i FC 27",
      headline: "Vind 1 kamp. Få <b>100 FC Points.</b>",
      headlineNav: "Vind 1 kamp.<br>Få <b>100 FC Points.</b>",
      headlineEvoBuilder:
        "Vil du lave en betalt evo?<br>Få <b>100 FC Points</b> med 1 sejr.",
      sub: "Red Bull Wings Cup Open: en gratis turnering i FC 27 på PS5. Op til 500 FC Points pr. bracket.",
      subEvoPlayers:
        "Red Bull Wings Cup Open på PS5. Op til 500 FC Points pr. bracket til din næste evo.",
      subNav:
        "Red Bull Wings Cup Open på PS5. Op til 500 FC Points pr. bracket. Slutter 31. okt.",
      badge: "første sejr",
      cta: "Tilmeld dig gratis →",
      menuHeading: "Tournaments",
      menuItem: "Wings Cup Open",
      menuBadge: "GRATIS",
    },
  },
};

export type WingsCupCopy = (typeof WINGS_CUP_CONFIG.copy)["en"];

/** Unit id -> which copy keys it uses. */
export const WINGS_CUP_UNITS: Record<
  WingsCupUnit,
  { headline: keyof WingsCupCopy; sub: keyof WingsCupCopy }
> = {
  sbc: { headline: "headline", sub: "sub" },
  gallery: { headline: "headline", sub: "sub" },
  players: { headline: "headline", sub: "sub" },
  "evo-players": { headline: "headline", sub: "subEvoPlayers" },
  "evo-builder": { headline: "headlineEvoBuilder", sub: "sub" },
  navigation: { headline: "headlineNav", sub: "subNav" },
};
